import {
  FlowOperationType,
  StepLocationRelativeToParent,
} from '@activepieces/shared';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { useBuilderStateContext } from '../../builder-hooks';
import { PieceSelector } from '../../pieces-selector';
import { CANVAS_PARAMETERS, flowUtilConsts } from '../utils/consts';
import { flowCanvasUtils } from '../utils/flow-canvas-utils';
import { ApNewStepNode } from '../utils/types';

function getBackgroundColor(
  readonly: boolean,
  pieceSelectorOpen: boolean
): string {
  if (readonly) {
    return '#ccc';
  }
  if (pieceSelectorOpen) {
    return '#ff9900';
  }
  return '#007AFF';
}

const ApNewStepCanvaNode = ({ id, data }: Omit<ApNewStepNode, 'position'>) => {
  const [readonly] = useBuilderStateContext((state) => [state.readonly]);
  const [pieceSelectorOpen, setPieceSelectorOpen] = useState(false);
  const { fitView } = useReactFlow();

  const onPieceSelectorOpenChange = (open: boolean) => {
    if (readonly) return
    
    if (open) {
      fitView({
        ...flowCanvasUtils.createFocusStepInGraphParams(id),
        duration: CANVAS_PARAMETERS.animationDuration,
      });
    }

    const timeoutId = setTimeout(
      () => setPieceSelectorOpen(open),
      open ? CANVAS_PARAMETERS.animationDuration : 0
    );

    return () => clearTimeout(timeoutId);
  };

  return (
    <PieceSelector
      operation={{
        type: FlowOperationType.ADD_ACTION,
        actionLocation: {
          parentStep: data.parentStepName ?? '',
          stepLocationRelativeToParent: StepLocationRelativeToParent.AFTER,
        },
        newStepPosition: data.newStepPosition,
      }}
      open={pieceSelectorOpen}
      onOpenChange={onPieceSelectorOpenChange}
      asChild
    >
      <div
        style={{
          width: flowUtilConsts.AP_NODE_SIZE.STEP.width / 2,
          height: flowUtilConsts.AP_NODE_SIZE.STEP.width / 2,
          margin: `${
            (flowUtilConsts.AP_NODE_SIZE.NEW_STEP_NODE.height -
              flowUtilConsts.AP_NODE_SIZE.STEP.width / 2) /
            2
          }px`,
          borderRadius: '50%',
          background: getBackgroundColor(readonly, pieceSelectorOpen),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: readonly ? 'default' : 'grab',
          pointerEvents: readonly ? 'none' : 'auto',
        }}
      >
        <Plus className="w-6 h-6 text-white" />
        <Handle
          type="source"
          style={{
            opacity: 0,
            cursor: 'default',
            width: 0,
            height: 0,
          }}
          position={Position.Right}
        />
        <Handle
          type="target"
          style={{
            opacity: 0,
            cursor: 'default',
            width: 0,
            height: 0,
          }}
          position={Position.Left}
        />
      </div>
    </PieceSelector>
  );
};

export default ApNewStepCanvaNode;
